import constants from '../constants';
import { EditableType } from '../../../constants';
import * as helpers from '../../helpers';
import { TagName, MediaElementSources, MediaElementData, MediaElementConfig, MediaElement } from '../../editableMedia/typing';
import { getVideoSettings } from '../../editableMedia/getData';

interface FrameData {
	frameWidth: number;
	frameHeight: number;
}

export type IndesignMediaElementConfig = MediaElementData & MediaElementConfig;

interface MediaData {
	items: IndesignMediaElementConfig[];
	isError: boolean;
}

export enum MediaElementDataAttribute {
	edited = 'data-edited', // is set to media when it was modified in backoffice
	label = 'data-label', // is set to store media name
	defaultLabel = 'data-default-label', // is set to store default media name
	tooltip = 'data-tooltip', // is set to store media tooltip text
	tooltipActive = 'data-tooltip-active', // is set when tooltip is active
	defaultLink = 'data-default-link', // is set to media to store default link
	driveLink = 'data-drive-link', // is set to media to store drive link
	externalLink = 'data-external-link', // is set to media to store external link
	externalLinkActive = 'data-external-link-active', // is set when external link is active
}

const isImageElement = (className: string): boolean => (
	className.includes(constants.imageClassName)
);

const isVideoElement = (tagName: string): boolean => tagName === TagName.video;

const isVideoIframeElement = (tagName: string, className: string): boolean => (
	tagName === TagName.iframe && className === constants.videoClassName
);

const getFrameData = (id: string, css: string): FrameData => {
	const { width, height } = helpers.getElementSizes(id, css);
	return {
		frameWidth: width,
		frameHeight: height,
	};
};

const getMediaType = ({ tagName, className }: MediaElement): EditableType => {
	switch (tagName) {
		case TagName.img:
		default:
			return EditableType.image;
		case TagName.video:
			return EditableType.video;
		case TagName.iframe:
			return className === constants.videoClassName
				? EditableType.video
				: EditableType.animation;
	}
};

const getElementId = (element: MediaElement): string => {
	if (isImageElement(element.className)) {
		return element.id;
	}

	const parentElement = element.closest(`[id*=${constants.parentContainerId}]`) as HTMLElement;
	return parentElement.id;
};

const getElementSources = (element: MediaElement, isEditedFromBackoffice: boolean): MediaElementSources => {
	if (isEditedFromBackoffice) {
		return {
			defaultLink: element.getAttribute(MediaElementDataAttribute.defaultLink) || '',
			driveLink: element.getAttribute(MediaElementDataAttribute.driveLink) || '',
			externalLink: element.getAttribute(MediaElementDataAttribute.externalLink) || '',
			isExternalLinkActive: element.getAttribute(MediaElementDataAttribute.externalLinkActive) === 'true',
		};
	}

	const { tagName, className } = element;
	const elementWithSrc = isVideoElement(tagName)
		? element.querySelector('source')
		: element;
	const { src } = elementWithSrc as MediaElement;
	const isVideoIframe = isVideoIframeElement(tagName, className);
	const defaultLink = helpers.getUrlWithoutOrigin(src);

	return {
		defaultLink,
		driveLink: '',
		externalLink: isVideoIframe ? defaultLink : '',
		isExternalLinkActive: isVideoIframe,
	};
};

const getElementLabels = (element: MediaElement, isEditedFromBackoffice: boolean): { label: string, defaultLabel: string } => {
	if (!isEditedFromBackoffice) {
		return {
			label: '',
			defaultLabel: element.getAttribute(MediaElementDataAttribute.label) || '',
		};
	}

	return {
		label: element.getAttribute(MediaElementDataAttribute.label) || '',
		defaultLabel: element.getAttribute(MediaElementDataAttribute.defaultLabel) || '',
	};
};

const getElementTooltip = (element: MediaElement, isEditedFromBackoffice: boolean): { tooltip: string, isTooltipActive: boolean } => {
	const tooltipActionAttribute = element.attributes['onmouseenter'] || {}; // tslint:disable-line no-string-literal
	const { value: defaultTooltipAction } = tooltipActionAttribute;
	const tooltip = element.getAttribute(MediaElementDataAttribute.tooltip) || helpers.getDefaultTooltip(defaultTooltipAction);

	return {
		tooltip,
		isTooltipActive: isEditedFromBackoffice
			? element.getAttribute(MediaElementDataAttribute.tooltipActive) === 'true'
			: !!tooltip,
	};
};

const getMediaData = (element: MediaElement, frameWidth: number, frameHeight: number): MediaElementData => {
	const mediaElement = isImageElement(element.className)
		? element.querySelector('img')
		: element;
	const {
		style: { width, height, transform },
	} = mediaElement as MediaElement;
	const [x, y] = helpers.getTranslateValueFromStyle({ transform });
	const isEditedFromBackoffice = mediaElement!.getAttribute(MediaElementDataAttribute.edited) === 'true';
	const { className, tagName } = element;

	return {
		type: getMediaType(element),
		width: width ? helpers.getNumberFromString(width) : frameWidth,
		height: height ? helpers.getNumberFromString(height) : frameHeight,
		x,
		y,
		isEditedFromBackoffice,
		canDragAndResize: isImageElement(element.className)
			? isEditedFromBackoffice
			: false,
		videoSettings: getVideoSettings(mediaElement as MediaElement, isVideoElement(tagName), isVideoIframeElement(tagName, className)),
		...getElementSources(mediaElement as MediaElement, isEditedFromBackoffice),
		...getElementLabels(mediaElement as MediaElement, isEditedFromBackoffice),
		...getElementTooltip(mediaElement as MediaElement, isEditedFromBackoffice),
	};
};

const getConfig = (element: MediaElement, page: number, css: string): IndesignMediaElementConfig => {
	const id = getElementId(element);
	const { frameWidth, frameHeight } = getFrameData(id, css);
	const mediaData = getMediaData(element, frameWidth, frameHeight);

	return {
		page,
		id,
		importManagerId: `${id}-${page}`,
		frameWidth,
		frameHeight,
		isLoaded: true,
		...mediaData,
	};
};

export default (page: number, htmlDocument: HTMLDocument, css: string): MediaData => {
	const items: IndesignMediaElementConfig[] = [];
	let isError: boolean = false;

	const editableMediaElements = [
		...htmlDocument.querySelectorAll(`[class*=${constants.imageClassName}]:not([class*=${constants.lockedClassName}])`),
		...htmlDocument.querySelectorAll(`[class*=${constants.videoClassName}]:not([class*=${constants.lockedClassName}])`),
	] as MediaElement[];

	try {
		editableMediaElements.forEach(element => (
			items.push(getConfig(element, page, css))
		));
	} catch (err) {
		isError = true;
	}

	return {
		items,
		isError,
	};
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/utils/indesignEditor/editableMedia/getData.ts